import { EnvironmentInjector, runInInjectionContext } from '@angular/core';
import {
  AngularFirestore,
  AngularFirestoreCollection,
  AngularFirestoreDocument,
} from '@angular/fire/compat/firestore';

/**
 * AngularFire v20's compat layer constructs `AngularFirestoreDocument` /
 * `AngularFirestoreCollection` (and `...CollectionGroup`) with an
 * `injector = inject(EnvironmentInjector)` field initializer. That means every
 * `.collection()` / `.doc()` call must run inside an active Angular injection
 * context, otherwise it throws `NG0203` ("inject() must be called from an
 * injection context").
 *
 * This app builds its Firestore references lazily inside lifecycle hooks,
 * `setTimeout`, and after `await` — all outside injection context — so without
 * this shim most authenticated data loads fail. (v7's compat wrappers did not
 * use `inject()`, so the pattern worked before the 12->20 upgrade.)
 *
 * The shim wraps only the reference-building methods so the wrapper construction
 * always runs inside the root EnvironmentInjector's context. The observable
 * methods (`get`/`valueChanges`/`snapshotChanges`/…) use the injector captured at
 * construction, so they need no wrapping once the reference is built in context.
 *
 * Bridge until the services are migrated to the modular AngularFire API.
 */
let patched = false;

export function patchAngularFirestoreInjectionContext(injector: EnvironmentInjector): void {
  if (patched) {
    return;
  }
  patched = true;

  const wrap = (proto: any, method: string): void => {
    const original = proto[method];
    if (typeof original !== 'function') {
      return;
    }
    proto[method] = function (this: unknown, ...args: unknown[]): unknown {
      return runInInjectionContext(injector, () => original.apply(this, args));
    };
  };

  wrap(AngularFirestore.prototype, 'collection');
  wrap(AngularFirestore.prototype, 'doc');
  wrap(AngularFirestore.prototype, 'collectionGroup');
  wrap(AngularFirestoreDocument.prototype, 'collection');
  wrap(AngularFirestoreCollection.prototype, 'doc');
}
